import React, { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import _ from "lodash";
import BeneficiarySearchNSelect from "../../Eligibility/components/requestEligibility/BeneficiarySearchNSelect";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../shared/CustomButton";
import { FaFilter, FaUndo } from "react-icons/fa";
import AppButton from "../../../shared/AppButton";
import CustomFilterInput from "../../../shared/CustomFilterInput";

function PreAuthFilter({
  refetch,
  setLoading,
  advancePreauth = false,
  setPreauthFilters,
  fromDate,
  toDate,
  preAuthFilters,
}) {
  const { t, i18n } = useTranslation();
  const [payers, setPayers] = useState([]);
  const [isApplying, setIsApplying] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const navigate = useNavigate();

  const addData = async (values) => {
    let payerName = "";
    if (advancePreauth && values.payer) {
      for (const category of payers) {
        const found = category?.payers?.find(
          (p) => String(p.id) === String(values.payer),
        );
        if (found) {
          payerName = found.name_en;
          break;
        }
      }
    }

    let payload = {
      from_date: values.fromDate,
      to_date: values.toDate,
      type: values.type,
      from_date_ordered: values?.fromDateOrdered,
      to_date_ordered: values?.toDateOrdered,
      status: values.status,
      pre_auth_ref_no: values.preAuthRefNo,
      ...(advancePreauth
        ? {
            payer_name: payerName,
            patient_name: values.beneficiaryName || "",
            request_bundle_id: values.bundleID,
            payer_id: values.payer,
            patient_id: values.beneficiary,
          }
        : {
            payer_id: values.payer,
            patient_id: values.beneficiary,
            pre_auth_bundle_id: values.bundleID,
            is_followup_req: values?.isFollowUp,
          }),
      limit: 10,
      page: 1,
    };

    let apiInput = Object.keys(payload).reduce((acc, key) => {
      const value = payload[key];
      if (advancePreauth && (key === "payer_id" || key === "patient_id")) {
        return acc;
      }
      if (value !== "" && value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {});

    try {
      setIsApplying(true);
      setLoading(true);
      await refetch({ input: apiInput });
    } finally {
      setLoading(false);
      setIsApplying(false);
    }

    // Keeping local storage update for backward compatibility if any other pages rely on it
    localStorage.setItem("preAuthFilterDate", JSON.stringify(payload));
    setPreauthFilters(payload);
  };

  const handleResetForm = async (values) => {
    let payload = {
      from_date: fromDate,
      to_date: toDate,
      type: "",
      from_date_ordered: "",
      to_date_ordered: "",
      status: "",
      pre_auth_ref_no: "",
      ...(advancePreauth
        ? {
            payer_name: "",
            patient_name: "",
            request_bundle_id: "",
            payer_id: "",
            patient_id: "",
          }
        : {
            payer_id: "",
            patient_id: "",
            pre_auth_bundle_id: "",
            is_followup_req: false,
          }),
      limit: 10,
      page: 1,
    };

    let apiInput = Object.keys(payload).reduce((acc, key) => {
      const value = payload[key];
      if (advancePreauth && (key === "payer_id" || key === "patient_id")) {
        return acc;
      }
      if (value !== "" && value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {});

    try {
      setIsResetting(true);
      setLoading(true);
      await refetch({ input: apiInput });
    } finally {
      setLoading(false);
      setIsResetting(false);
    }
    localStorage.setItem("preAuthFilterDate", JSON.stringify(payload));
    setPreauthFilters(payload);
  };

  React.useEffect(() => {
    const payersData = JSON.parse(localStorage.getItem("payersData"));

    if (payersData && payersData.length > 0) {
      setPayers(payersData);
    } else {
      navigate("/dashboard");
    }
  }, []);

  const types = [
    { id: "institutional", name: "Institutional" },
    { id: "oral", name: "Dental" },
    { id: "pharmacy", name: "Pharmacy" },
    { id: "professional", name: "Professional" },
    { id: "vision", name: "Optical" },
  ];

  const statuses = [
    { id: "approved", name: "Approved" },
    { id: "partial Approved", name: "Partial Approved" },
    { id: "rejected", name: "Rejected" },
    { id: "pending", name: "Pending" },
    { id: "pended", name: "Pended" },
  ];
  const outcome = [
    { id: "error", name: "Error" },
    { id: "queued", name: "Queued" },
    { id: "complete", name: "Complete" },
  ];

  return (
    <Formik
      initialValues={{
        fromDate: preAuthFilters?.from_date || fromDate,
        toDate: preAuthFilters?.to_date || toDate,
        fromDateOrdered: preAuthFilters?.from_date_ordered || "",
        toDateOrdered: preAuthFilters?.to_date_ordered || "",
        payer: advancePreauth
          ? preAuthFilters?.payer_id || preAuthFilters?.payer_name || ""
          : preAuthFilters?.payer_id || preAuthFilters?.payer || "",
        type: preAuthFilters?.type || "",
        beneficiary: advancePreauth
          ? preAuthFilters?.patient_id || preAuthFilters?.patient_name || ""
          : preAuthFilters?.patient_id || "",
        beneficiaryName: preAuthFilters?.patient_name || "",
        preAuthorizationID: preAuthFilters?.pre_auth_id || "",
        status: preAuthFilters?.status || "",
        preAuthRefNo: preAuthFilters?.pre_auth_ref_no || "",
        bundleID: advancePreauth
          ? preAuthFilters?.request_bundle_id || ""
          : preAuthFilters?.pre_auth_bundle_id || "",
        outcome: preAuthFilters?.outcome || "",
        isFollowUp: preAuthFilters?.is_followup_req || false,
        // isValid: true,
      }}
      validate={(values) => {
        const errors = {};
      }}
      onSubmit={(values, { setSubmitting }) => {
        addData(values);
      }}
      onReset={(values, { setSubmitting }) => {
        handleResetForm(values);
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        resetForm,
        /* and other goodies */
      }) => (
        <>
          <div className="w-full">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-x-1.5 gap-y-1.5">
              <CustomFilterInput
                label={t("Submission Date")}
                name="fromDate"
                value={values.fromDate}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("From Date")}
                type="date"
              />

              <CustomFilterInput
                label={t("To Date")}
                name="toDate"
                value={values.toDate}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("To Date")}
                type="date"
              />

              <CustomFilterInput
                label={t("Ordered Date")}
                name="fromDateOrdered"
                value={values.fromDateOrdered}
                onChange={handleChange}
                placeholder={t("From Order Date")}
                type="text"
                onFocus={(e) => (e.target.type = "date")}
                onBlur={(e) => {
                  e.target.type = "text";
                  handleBlur(e);
                }}
              />

              <CustomFilterInput
                label={t("To Order Date")}
                name="toDateOrdered"
                value={values.toDateOrdered}
                onChange={handleChange}
                placeholder={t("To Order Date")}
                type="text"
                onFocus={(e) => (e.target.type = "date")}
                onBlur={(e) => {
                  e.target.type = "text";
                  handleBlur(e);
                }}
              />

              <CustomFilterInput
                label={t("Select Payer")}
                name="payer"
                value={values.payer}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("Select Payer")}
                isSelect
                renderOptions={() =>
                  payers.map((category) => (
                    <optgroup
                      key={category.id}
                      label={
                        category?.name_en
                          ? `${category?.name_en} (${category?.license_id})`
                          : ""
                      }
                    >
                      {category?.payers?.map((insurance, idx) => (
                        <option
                          key={`${insurance.id}-${idx}`}
                          value={insurance.id}
                        >
                          {insurance?.name_en} ({insurance?.license_id})
                        </option>
                      ))}
                    </optgroup>
                  ))
                }
              />

              <CustomFilterInput
                label={t("Select Type")}
                name="type"
                value={values.type}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("Select Type")}
                isSelect
                options={types}
              />

              <CustomFilterInput
                label={t("Pre Authorization ID")}
                name="preAuthorizationID"
                value={values.preAuthorizationID}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("Pre Authorization ID")}
                type="text"
              />

              <CustomFilterInput
                label={t("Select Status")}
                name="status"
                value={values.status}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("Select Status")}
                isSelect
                options={statuses}
              />

              <CustomFilterInput
                label={t("Select Outcome")}
                name="outcome"
                value={values.outcome}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("Select Outcome")}
                isSelect
                options={outcome}
              />

              <CustomFilterInput
                label={t("Pre Auth Reference No")}
                name="preAuthRefNo"
                value={values.preAuthRefNo}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("Pre Auth Reference No")}
                type="text"
              />

              <CustomFilterInput
                label={t("Request Bundle ID")}
                name="bundleID"
                value={values.bundleID}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={t("Request Bundle ID")}
                type="text"
              />
              {!advancePreauth && (
                <div className="flex flex-col">
                  <label className="block font-semibold text-[12px] text-gray-700 mb-0.5 capitalize">
                    {t("Is Follow Up")}
                  </label>
                  <div className="flex justify-between items-center bg-slate-100/50 border !border-slate-200 rounded-[6px] px-2 !h-[28px] !py-0">
                    <div className="text-xs font-medium text-gray-700 capitalize tracking-tight">
                      {t("Is Follow Up")}
                    </div>

                    <div className="flex items-center">
                      <label
                        htmlFor="toggle-born"
                        className="relative inline-flex items-center cursor-pointer scale-[0.65] origin-right"
                      >
                        <input
                          type="checkbox"
                          id="toggle-born"
                          name="isFollowUp"
                          className="sr-only peer"
                          checked={values.isFollowUp}
                          onChange={handleChange}
                        />

                        <div
                          className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer
                            peer-checked:!bg-[#0070C2]
                            peer-checked:after:translate-x-[20px]
                            rtl:peer-checked:after:-translate-x-[20px]
                            after:content-[''] after:absolute after:top-[2px] after:start-[2px]
                            after:bg-white after:border-gray-300 after:border after:rounded-full
                            after:h-5 after:w-5 after:transition-all after:duration-300"
                        ></div>
                      </label>
                    </div>
                  </div>
                </div>
              )}
              <div className={advancePreauth ? "col-span-2" : "col-span-1"}>
                <label className="block font-semibold text-[12px] text-gray-700 mb-0.5 capitalize truncate">
                  {t("Search Patient")}
                </label>
                <BeneficiarySearchNSelect
                  setFieldValue={(val) => {
                    setFieldValue("beneficiary", val.id);
                    setFieldValue("beneficiaryName", val.name);
                  }}
                  name="eligibility"
                />
              </div>
              <div className="flex justify-end gap-2 items-end">
                <AppButton
                  title={t("Reset")}
                  variant="reset"
                  onClick={resetForm}
                  startIcon={<FaUndo className="!text-[12px]" />}
                  className="hover:!bg-slate-100"
                  isLoading={isResetting}
                  loadingText=""
                />

                <AppButton
                  title={t("Apply Filter")}
                  onClick={() => {
                    handleSubmit();
                  }}
                  startIcon={<FaFilter className="!text-[12px]" />}
                  isLoading={isApplying}
                  loadingText=""
                />
              </div>
            </div>
          </div>
        </>
      )}
    </Formik>
  );
}

export default PreAuthFilter;
